import { FadeUp } from "@/components/motion";
import { ArrowRight } from "lucide-react";

const STEPS = [
  {
    label: "Target",
    body: "Define the roles, level and professional direction you want to pursue.",
  },
  {
    label: "Align",
    body: "Identify the experience, capabilities and transferable strengths relevant to those roles.",
  },
  {
    label: "Substantiate",
    body: "Support the positioning with evidence: achievements, outcomes, scale, complexity and impact.",
  },
  {
    label: "Position",
    body: "Translate the evidence into a clear professional narrative across the CV and LinkedIn profile.",
    outcome: true,
  },
];

export default function Standard() {
  return (
    <section data-testid="standard" id="standard" className="relative bg-[#0A0A0A] py-28 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp className="mb-14 md:mb-20 max-w-3xl">
          <p className="tracking-label text-[11px] uppercase font-body font-bold text-[#DFFF00] mb-6">The Standard</p>
          <h2 className="font-display font-bold tracking-tighter leading-[0.95] text-4xl md:text-6xl text-[#F8F8F5]">
            Experience is only valuable when it is positioned <span className="font-serif-accent font-light">strategically.</span>
          </h2>
          <p className="mt-8 font-body text-base md:text-lg text-[#A1A1AA] leading-relaxed max-w-2xl">
            Your CV and LinkedIn profile should do more than describe where you've been. They should build
            a credible case for where you're going — aligning your experience, skills, value and evidence
            with the roles you want to target.
          </p>
        </FadeUp>

        <div className="grid md:grid-cols-4 gap-4 md:gap-0 items-stretch" data-testid="standard-pathway">
          {STEPS.map((s, i) => (
            <FadeUp key={s.label} delay={i * 0.08} className="relative flex">
              {i > 0 && (
                <span className="hidden md:flex absolute left-[-12px] top-1/2 -translate-y-1/2 z-20 w-6 h-6 items-center justify-center bg-[#0A0A0A]">
                  <ArrowRight size={16} className="text-[#DFFF00]" />
                </span>
              )}
              <div
                data-testid={`standard-step-${s.label.toLowerCase()}`}
                className={`flex flex-col w-full p-7 md:p-8 border transition-colors duration-300 md:mr-[-1px] ${
                  s.outcome
                    ? "border-[#DFFF00] bg-[#DFFF00]/5 relative z-10"
                    : "border-[#27272A] bg-[#141414]"
                }`}
              >
                {s.outcome && (
                  <span className="tracking-label text-[9px] uppercase font-body font-bold text-[#0A0A0A] bg-[#DFFF00] px-2 py-1 self-start mb-4">
                    The outcome
                  </span>
                )}
                <span className={`font-display text-xl md:text-2xl font-bold tracking-tight uppercase ${s.outcome ? "text-[#DFFF00]" : "text-[#F8F8F5]"}`}>
                  {s.label}
                </span>
                <p className="mt-3 font-body text-sm text-[#A1A1AA] leading-relaxed">{s.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.2}>
          <p className="mt-14 md:mt-20 font-display font-bold tracking-tighter text-2xl md:text-4xl text-[#F8F8F5] max-w-3xl">
            Not a better-looking CV. A stronger case for why you{" "}
            <span className="font-serif-accent font-light text-[#DFFF00]">belong in the role.</span>
          </p>
        </FadeUp>
      </div>
    </section>
  );
}
